import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { collection, query, getDocs, orderBy, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { MagnifyingGlassIcon, FunnelIcon, XMarkIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Counter from "yet-another-react-lightbox/plugins/counter";
import "yet-another-react-lightbox/plugins/counter.css";
import { Link } from 'react-router-dom';

// 定义教师类型
interface Teacher {
    id: string;
    name: string;
    nameEn?: string;
    title: string;
    titleEn?: string;
    photo: string;
    poster: string;  // 添加海报字段
    bio: string;
    bioEn?: string;
    education: string[];
    specialties: string[];
    email?: string;
    order?: number;
    isActive?: boolean;
}

const TeachersPage: React.FC = () => {
    const { t, i18n } = useTranslation();
    const [teachers, setTeachers] = useState<Teacher[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const isEnglish = i18n.language === 'en';
    
    // 添加 Lightbox 相关状态
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [selectedTeacher, setSelectedTeacher] = useState<Teacher | null>(null);

    // 打开教师详情
    const openTeacherDetail = (teacher: Teacher) => {
        setSelectedTeacher(teacher);
        setLightboxOpen(true);
    };

    useEffect(() => {
        const fetchTeachers = async () => {
            try {
                setLoading(true);
                setError(null);
                
                // 创建查询
                const teachersQuery = query(
                    collection(db, 'teachers'),
                    where('isActive', '==', true),
                    orderBy('order', 'asc'), // 按照顺序排序
                    orderBy('name', 'asc'), // 然后按照姓名排序
                );
                
                const querySnapshot = await getDocs(teachersQuery);
                
                if (querySnapshot.empty) {
                    setTeachers([]);
                    setLoading(false);
                    return;
                }
                
                // 转换数据
                const teachersData: Teacher[] = querySnapshot.docs.map(doc => {
                    const data = doc.data();
                    return {
                        id: doc.id,
                        name: data.name || '',
                        nameEn: data.nameEn || '',
                        title: data.title || '',
                        titleEn: data.titleEn || '',
                        photo: data.photo || '',
                        poster: data.poster || data.photo || '', // 如果没有海报，使用照片作为备选
                        bio: data.bio || '',
                        bioEn: data.bioEn || '',
                        education: data.education || [],
                        specialties: data.specialties || [],
                        email: data.email || '',
                        order: data.order || 999
                    };
                });
                
                setTeachers(teachersData);
            } catch (err) {
                console.error('获取教师数据失败:', err);
                setError(isEnglish ? 'Failed to fetch teachers data' : '获取教师数据失败，请稍后再试');
            } finally {
                setLoading(false);
            }
        };
        
        fetchTeachers();
    }, [isEnglish]);

    // 获取展示的教师名称（根据语言）
    const getTeacherName = (teacher: Teacher) => {
        return isEnglish && teacher.nameEn ? teacher.nameEn : teacher.name;
    };
    
    // 获取展示的职称（根据语言）
    const getTeacherTitle = (teacher: Teacher) => {
        return isEnglish && teacher.titleEn ? teacher.titleEn : teacher.title;
    };
    
    // 获取展示的简介（根据语言）
    const getTeacherBio = (teacher: Teacher) => {
        return isEnglish && teacher.bioEn ? teacher.bioEn : teacher.bio;
    };

    // 准备 Lightbox 的图片
    const lightboxSources = selectedTeacher ? [{ src: selectedTeacher.poster || selectedTeacher.photo }] : [];

    return (
        <div className="bg-white mt-32">
            {/* 页面标题区域 */}
            <div className="bg-gradient-to-r from-blue-800 to-indigo-900 py-16 text-center">
                <motion.h1 
                    className="text-4xl font-bold text-white mb-4"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    {t('teachers.title', '教师团队')}
                </motion.h1>
                <motion.p 
                    className="text-xl text-blue-100 max-w-3xl mx-auto px-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                >
                    {t('teachers.subtitle', '万湖书院拥有一支专业、敬业、充满热情的教师团队')}
                </motion.p>
            </div>
            
            {/* 主要内容区域 */}
            <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
                {/* 教师团队介绍 */}
                <motion.section 
                    className="mb-12"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                >
                    <h2 className="text-3xl font-bold text-gray-900 mb-6 border-b border-gray-200 pb-2">
                        {t('teachers.introduction.title', '我们的教师团队')}
                    </h2>
                    <p className="text-lg text-gray-700 mb-4">
                        {t('teachers.introduction.paragraph1', '万湖书院拥有一支由资深教育专家和优秀教师组成的教师团队。我们的教师不仅具备扎实的专业知识和丰富的教学经验，还充满教育热情和创新精神。')}
                    </p>
                    <p className="text-lg text-gray-700 mb-4">
                        {t('teachers.introduction.paragraph2', '我们的教师团队中，有国家级特级教师、省市级骨干教师，也有海外留学归来的青年才俊。他们在各自的专业领域都有深厚的造诣，并致力于将最新的教育理念和方法应用到教学实践中。')}
                    </p>
                    <p className="text-lg text-gray-700">
                        {t('teachers.introduction.paragraph3', '在万湖书院，教师不仅是知识的传授者，更是学生成长的引导者和陪伴者。我们的教师关注每一位学生的个性发展，尊重学生的独特性，帮助他们发现自己的潜能和兴趣，为他们的未来奠定坚实的基础。')}
                    </p>
                </motion.section>

                {/* 载入状态 */}
                {loading && (
                    <div className="py-20 text-center">
                        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
                        <p className="mt-4 text-lg text-gray-600">{t('common.loading', '加载中...')}</p>
                    </div>
                )}

                {/* 错误状态 */}
                {error && (
                    <div className="text-center py-20 bg-red-50 rounded-lg">
                        <p className="text-red-600 text-lg">
                            {error}
                        </p>
                        <button
                            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                            onClick={() => window.location.reload()}
                        >
                            {t('common.retry', '重试')}
                        </button>
                    </div>
                )}

                {/* 教师列表 */}
                {!loading && !error && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                    {teachers.map((teacher, index) => (
                        <motion.div 
                            key={teacher.id}
                            className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: index * 0.1 }}
                            whileHover={{ y: -5 }}
                        >
                            <div 
                                className="h-64 overflow-hidden cursor-pointer relative group"
                                onClick={() => openTeacherDetail(teacher)}
                            >
                                <img 
                                    src={teacher.photo} 
                                    alt={getTeacherName(teacher)} 
                                    className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-110"
                                />
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 flex items-center justify-center transition-opacity duration-300">
                                    <span className="text-white opacity-0 group-hover:opacity-100 bg-blue-600 px-4 py-2 rounded-full">
                                        {t('teachers.viewDetails', '查看详情')}
                                    </span>
                                </div>
                            </div>
                            <div className="p-6">
                                <div className="mb-2">
                                    <h3 className="text-xl font-bold text-gray-900">{getTeacherName(teacher)}</h3>
                                </div>
                                <p className="text-gray-600 mb-4">{getTeacherTitle(teacher)}</p>
                                <p className="text-gray-700 mb-4 line-clamp-3">{getTeacherBio(teacher)}</p>
                                <div className="mb-4">
                                    <h4 className="font-medium text-gray-900 mb-1">{t('teachers.education', '教育背景')}</h4>
                                    <ul className="list-disc list-inside text-sm text-gray-700">
                                        {teacher.education.map((edu, i) => (
                                            <li key={i}>{edu}</li>
                                        ))}
                                    </ul>
                                </div>
                                <div className="mb-4">
                                    <h4 className="font-medium text-gray-900 mb-1">{t('teachers.specialties', '教授课程')}</h4>
                                    <div className="flex flex-wrap gap-2">
                                        {teacher.specialties.map((specialty, i) => (
                                            <span key={i} className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded-full">
                                                {specialty}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                                {/* {teacher.email && (
                                    <div>
                                        <h4 className="font-medium text-gray-900 mb-1">{t('teachers.contact', '联系方式')}</h4>
                                        <a href={`mailto:${teacher.email}`} className="text-blue-600 hover:text-blue-800 text-sm">
                                            {teacher.email}
                                        </a>
                                    </div>
                                )} */}
                                <div className="mt-4 flex justify-end items-center gap-2 cursor-pointer text-blue-600 hover:text-blue-800" onClick={() => openTeacherDetail(teacher)}>
                                    {t('teachers.viewDetails', '查看详情')} 
                                    <ArrowRightIcon className="w-4 h-4" />
                                </div>
                            </div>
                        </motion.div>
                    ))}
                    </div>
                )}

                {/* 加入我们 */}
                <motion.section 
                    className="mt-16 bg-blue-50 p-8 rounded-lg"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                >
                    <h2 className="text-2xl font-bold text-blue-900 mb-4">
                        {t('teachers.joinUs.title', '加入我们的教师团队')}
                    </h2>
                    <p className="text-lg text-blue-800 mb-6">
                        {t('teachers.joinUs.description', '万湖书院始终欢迎优秀的教育工作者加入我们的团队。如果您热爱教育，有创新精神，并希望在一个充满活力的环境中工作，请与我们联系。')}
                    </p>
                    <Link 
                        to="/contact" 
                        className="inline-block px-6 py-3 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition-colors"
                    >
                        {t('teachers.joinUs.contactUs', '联系我们')}
                    </Link>
                </motion.section>
            </div>

            {/* Lightbox 组件 */}
            <Lightbox
                open={lightboxOpen}
                close={() => setLightboxOpen(false)}
                slides={lightboxSources}
                plugins={[Zoom, Counter]}
                carousel={{ finite: true }}
                animation={{ fade: 300 }}
                styles={{ 
                    container: { backgroundColor: "rgba(0, 0, 0, 0.9)" }
                }}
                render={{
                    buttonPrev: () => null,
                    buttonNext: () => null,
                    iconZoomIn: () => <span className="text-white">+</span>,
                    iconZoomOut: () => <span className="text-white">-</span>
                }}
            />

            {/* 教师详情模态框 */}
            {lightboxOpen && selectedTeacher && (
                <div className="fixed bottom-0 left-0 right-0 bg-white p-4 shadow-lg rounded-t-lg z-[1001] max-h-60 overflow-y-auto">
                    <div className="mb-2">
                        <h2 className="text-2xl font-bold text-gray-900">{getTeacherName(selectedTeacher)}</h2>
                    </div>
                    <p className="text-gray-600 mb-2">{getTeacherTitle(selectedTeacher)}</p>
                    <p className="text-gray-700 mb-4">{getTeacherBio(selectedTeacher)}</p>
                </div>
            )}
        </div>
    );
};

export default TeachersPage; 